import {Clipboard} from '@angular/cdk/clipboard';
import {AsyncPipe} from '@angular/common';
import {Component, OnInit} from '@angular/core';
import {MatIconButton} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {MatDivider} from '@angular/material/divider';
import {MatIcon} from '@angular/material/icon';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatSnackBar, MatSnackBarRef} from '@angular/material/snack-bar';
import {MatTooltip} from '@angular/material/tooltip';
import {AssetService, AssetType} from '@app/asset';
import {AssetLinkService} from '@app/asset-link/asset-link.service';
import {DownloadService} from '@app/download/download.service';
import {GroupService} from '@app/group/group.service';
import {SelectionService} from '@app/selection/selection.service';
import {DeleteAssetDialogComponent} from '@app/shared/delete-asset-dialog/delete-asset-dialog.component';
import {StorageService} from '@app/shared/storage.service';
import {StateService} from '@app/state/state.service';
import {Stream} from '@app/stream/models';
import {StreamMoveMenuComponent} from '@app/stream/stream-move-menu/stream-move-menu.component';
import {StreamService} from '@app/stream/stream.service';
import {MultiEditDialogComponent} from '@app/tag/multi-edit-dialog/multi-edit-dialog.component';
import {Observable} from 'rxjs';
import {filter, map, startWith, take} from 'rxjs/operators';

@Component({
    selector: 'frog-selection-snackbar',
    templateUrl: './selection-snackbar.component.html',
    styleUrls: ['./selection-snackbar.component.scss'],
    imports: [
        MatIconButton,
        MatIcon,
        MatTooltip,
        MatMenuTrigger,
        MatMenu,
        MatMenuItem,
        StreamMoveMenuComponent,
        MatDivider,
        AsyncPipe,
    ],
})
export class SelectionSnackbarComponent implements OnInit {
    public selection$: Observable<string[]>;
    public stateStream$: Observable<string>;
    public streams$: Observable<Stream[]>;

    constructor(
        private selectionService: SelectionService,
        private groupService: GroupService,
        private assetService: AssetService,
        private stateService: StateService,
        private streamService: StreamService,
        private storageService: StorageService,
        private assetLinkService: AssetLinkService,
        private downloadService: DownloadService,
        private matDialog: MatDialog,
        private matSnackBar: MatSnackBar,
        private clipboard: Clipboard,
        public snackBarRef: MatSnackBarRef<SelectionSnackbarComponent>,
    ) {}

    ngOnInit(): void {
        this.selection$ = this.selectionService.selection.pipe(startWith([]));
        this.stateStream$ = this.stateService.state.pipe(
            filter((state) => Boolean(state.streamId)),
            map((state) => state.streamId),
        );
        this.streams$ = this.streamService.streams.pipe(
            map((streams) => streams.filter((stream) => stream.parent === null)),
            map((streams) => streams.sort()),
        );
    }

    clearSelection() {
        this.selectionService.clearSelection();
        this.snackBarRef.dismiss();
        this.selectionService.setSelectMode(false);
    }

    groupSelection(assetIds: string[], streamId: string) {
        this.assetService.resolve(assetIds).subscribe((assets) => {
            // First, try to find a group in the selection
            const group = assets.find((asset) => asset.asset_type === AssetType.Group);
            if (group) {
                const assetIds = assets
                    .filter((asset) => asset.asset_type !== AssetType.Group)
                    .map((asset) => asset.id);
                this.groupService.append(group.id, assetIds).subscribe();
            } else {
                // No group found, so we create one
                const assetIds = assets.map((asset) => asset.id);
                this.groupService
                    .create(assetIds, streamId)
                    .subscribe((groupAsset) => this.assetService.filter(true));
            }

            this.clearSelection();
        });
    }

    download(assetIds: string[]) {
        this.downloadService.downloadAssets(assetIds);
        this.clearSelection();
    }

    delete(assetIds: string[]) {
        const dialog = this.matDialog.open(DeleteAssetDialogComponent, {
            width: '80vw',
            data: assetIds,
        });
        dialog.afterClosed().subscribe((result) => {
            if (result === null) {
                return;
            }

            if (result === true) {
                // Remove from all
                this.assetService.deleteAssets(assetIds);
            } else {
                // Remove form this stream
                this.stateService.state
                    .pipe(
                        filter((state) => Boolean(state.streamId)),
                        take(1),
                    )
                    .subscribe((state) => {
                        this.streamService.removeAssets(state.streamId, assetIds);
                        this.assetService.removeAssets(assetIds);
                    });
            }
            this.clearSelection();
        });
    }

    getLink(assetIds: string[], streamId: string) {
        this.assetLinkService.create(assetIds, streamId).subscribe((assetLink) => {
            this.clipboard.copy(assetLink.url);
            this.matSnackBar.open('Link copied', null, { duration: 2000 });
            this.clearSelection();
        });
    }

    moveToStream(assetIds: string[], streamId: string, fromStreamId: string) {
        this.streamService.assignAssets(streamId, assetIds, fromStreamId).subscribe(() => {
            this.assetService.removeAssets(assetIds);
            this.matSnackBar.open('Asset(s) moved', null, { duration: 2000 });
            this.clearSelection();
        });
    }

    copyToStream(assetIds: string[], streamId: string) {
        this.streamService.assignAssets(streamId, assetIds).subscribe(() => {
            this.matSnackBar.open('Asset(s) copied', null, { duration: 2000 });
            this.clearSelection();
        });
    }

    openEditTags(assetIds: string[]) {
        this.matDialog.open(MultiEditDialogComponent, { data: assetIds });
    }
}
